import React, {
    useState,
    useEffect,
    useRef,
    forwardRef,
    useImperativeHandle,
} from 'react';
import './SellerMyProfileForm.scss';
import { Form } from '@availity/form';
import { useStateSelector } from '../../../store/selectors';
import { LocationType } from '../../../slices/marketplace/timeline/models/enums/location-type.enum';
import { MarketplacePracticeProfilesContainer } from '../../../containers/MarketplacePracticeProfilesContainer/MarketplacePracticeProfilesContainer';
import { getSellerProfileValidationSchema } from '../MarketplaceSellerProfile.validation';
import { EntityLocationInfo } from '../../../slices/profiles-data-slice/models/entity-location-info';
import { PracticeProfileInfo } from '../../../slices/profiles-data-slice/models/practice-profile-info';
import { MarketplaceSellerProfile } from '../../../slices/profiles-data-slice/models/marketplace-seller-profile';
import { FormikValues } from 'formik';
import { useDispatch } from 'react-redux';
import { UpdateProfile } from '../../../slices/profiles-data-slice/thunks/update-profile.thunk';
import { getEmptyGuid } from '../../../utils/typedHelper';
import { useServiceFocuses } from '../../../custom-hooks/useServiceFocuses';
import { ProfileRatings } from '../../../slices/profiles-data-slice/models/marketplace-seller-profile';

interface SellerMyProfileFormProps {
    setIsSaving(value: boolean): void;
    isEditable?: boolean;
    isAdmin?: boolean;
    timelineId?: number;
    isBroker?: boolean;
    onSaveSuccess?: () => void;
    onSaveError?: () => void;
}

export interface SellerMyProfileFormRef {
    handleSubmit: () => void;
    getSelectedPracticeId: () => string;
}


const SellerMyProfileForm = forwardRef<
    SellerMyProfileFormRef,
    SellerMyProfileFormProps
>((props, ref) => {
    const { isEditable = true, isAdmin = false, isBroker = false, timelineId } = props;
    const serviceFocuses = useServiceFocuses();
    const axios = useStateSelector((state) => state.core.axios);
    const dispatch = useDispatch() as any;

    const parseRating = (v: unknown): number | undefined => {
        if (v === '' || v == null) return undefined;
        const n = Number(v);
        return isNaN(n) ? undefined : n;
    };

    const roundRating = (v: unknown): number | undefined => {
        const n = parseRating(v);
        if (n === undefined) return undefined;
        const clamped = Math.min(5, Math.max(0, n));
        return parseFloat(
            (Math.round(clamped * 10) / 10).toFixed(1)
        );
    };

    const roundRatings = (
        r: ProfileRatings | undefined
    ): ProfileRatings | undefined => {
        if (!r) return undefined;
        const google = roundRating(r.google);
        const facebook = roundRating(r.facebook);
        const yelp = roundRating(r.yelp);
        const averageRating = roundRating(r.averageRating);
        if (
            google === undefined &&
            facebook === undefined &&
            yelp === undefined &&
            averageRating === undefined
        )
            return {};
        return { ...r, google, facebook, yelp, averageRating };
    };

    const sellerProfile = useStateSelector(
        (state) => state.profilesData.selectedPofile
    ) as MarketplaceSellerProfile;
    const [editSellerProfile, setEditSellerProfile] =
        useState<MarketplaceSellerProfile>(sellerProfile);
    const [dataSyncTrigger, setDataSyncTrigger] = useState(0);
    const zipCodeCache = useRef<Record<string, boolean>>({}).current;
    const formRef = useRef<any>(null);
    const selectedPracticeIdRef = useRef<string>('');

    const buildRatingsInitialValues = (ratings: ProfileRatings | undefined): ProfileRatings => {
        if (!ratings) return {};
        const rounded = roundRatings(ratings);
        return {
            ...(rounded || {}),
            googleReviewCount: ratings.googleReviewCount,
            facebookReviewCount: ratings.facebookReviewCount,
            yelpReviewCount: ratings.yelpReviewCount,
            averageRating: rounded?.averageRating,
        };
    };

    useEffect(() => {
        if (sellerProfile) {
            setEditSellerProfile(sellerProfile);
            if (formRef.current) {
                formRef.current.setValues({
                    practiceProfiles: (sellerProfile.practiceProfiles || []).map((p) => ({
                        ...p,
                        ratings: buildRatingsInitialValues(p.ratings),
                    })),
                });
            }
        }
    }, [sellerProfile]);

    const updatePracticeProfile = (
        practiceIndex: number,
        field: string,
        value: any
    ): MarketplaceSellerProfile => {
        if (!editSellerProfile) {
            throw new Error('No seller profile available for update');
        }

        const updatedProfile: MarketplaceSellerProfile = {
            ...editSellerProfile,
            practiceProfiles:
                editSellerProfile.practiceProfiles?.map((practice, idx) => {
                    if (idx !== practiceIndex) {
                        return {
                            ...practice,
                            practiceLocations: practice.practiceLocations
                                ? [...practice.practiceLocations]
                                : [],
                            ratings: practice.ratings ? { ...practice.ratings } : {},
                        };
                    }
                    return {
                        ...practice,
                        practiceLocations: practice.practiceLocations
                            ? [...practice.practiceLocations]
                            : [],
                        id: practice?.id || `temp_${idx}`,
                        [field]: value,
                    } as PracticeProfileInfo;
                }) || [],
        };

        return updatedProfile;
    };

    const updatePracticeLocation = (
        practiceIndex: number,
        locationIndex: number,
        field: string,
        value: any
    ): MarketplaceSellerProfile => {
        if (!editSellerProfile) {
            throw new Error('No seller profile available for update');
        }

        // Deep copy the practices array to avoid shared references
        const updatedProfile: MarketplaceSellerProfile = {
            ...editSellerProfile,
            practiceProfiles:
                editSellerProfile.practiceProfiles?.map((practice, idx) => {
                    if (idx !== practiceIndex) {
                        // Deep copy other practices to prevent reference sharing
                        return {
                            ...practice,
                            practiceLocations: practice.practiceLocations
                                ? [...practice.practiceLocations]
                                : [],
                        };
                    }

                    // Deep copy the target practice and its locations
                    const updatedLocations = practice.practiceLocations
                        ? practice.practiceLocations.map((loc, locIdx) => {
                              if (locIdx === locationIndex) {
                                  return {
                                      ...loc,
                                      [field]: value,
                                  };
                              }
                              return { ...loc };
                          })
                        : [];

                    return {
                        ...practice,
                        practiceLocations: updatedLocations,
                    } as PracticeProfileInfo;
                }) || [],
        };

        return updatedProfile;
    };

    const calculatePracticeFteDvms = (
        practiceIndex: number,
        updatedLocationIndex?: number,
        updatedLocationValue?: any
    ): number => {
        const currentFormValues = formRef.current.values;
        const practiceLocations =
            currentFormValues.practiceProfiles?.[practiceIndex]
                ?.practiceLocations || [];

        const locationsFteDvms = practiceLocations?.map(
            (location: EntityLocationInfo, index: number) => {
                if (
                    updatedLocationIndex !== undefined &&
                    index === updatedLocationIndex
                ) {
                    return parseFloat(updatedLocationValue) || 0;
                }
                // Otherwise use the existing value (convert to number if needed)
                return Number(location.fteDvms) || 0;
            }
        );

        // Calculate sum using reduce method with proper typing
        const fteDvmsSum = locationsFteDvms.reduce(
            (sum: number, value: number) => sum + value,
            0
        );

        return fteDvmsSum;
    };

    const updatePracticeFteDvmsTotal = (
        practiceIndex: number,
        totalFteDvms: number
    ): void => {
        const practiceFteDvmsFieldName = `practiceProfiles[${practiceIndex}].fteDvms`;
        formRef.current.setFieldValue(practiceFteDvmsFieldName, totalFteDvms);

        // Update the profile state as well
        const profileWithCalculatedFteDvms = updatePracticeProfile(
            practiceIndex,
            'fteDvms',
            totalFteDvms
        );
        setEditSellerProfile(profileWithCalculatedFteDvms);
    };

    const updateFormValue = (name: string, value: any): void => {
        // Handle practice location field updates
        const locationMatches = name.match(
            /^practiceProfiles\[(\d+)\]\.practiceLocations\[(\d+)\]\.(.+)$/
        );

        if (locationMatches) {
            const [, practiceIndexStr, locationIndex, field] = locationMatches;
            const practiceIndex = parseInt(practiceIndexStr);
            const updatedProfile = updatePracticeLocation(
                practiceIndex,
                parseInt(locationIndex),
                field,
                value
            );
            setEditSellerProfile(updatedProfile);

            // Recalculate practice FTE DVMs total whenever any location field changes
            const totalFteDvms = calculatePracticeFteDvms(
                practiceIndex,
                field === 'fteDvms' ? parseInt(locationIndex) : undefined,
                field === 'fteDvms' ? value : undefined
            );
            updatePracticeFteDvmsTotal(practiceIndex, totalFteDvms);
        }

        const practiceRatingsMatches = name.match(
            /^practiceProfiles\[(\d+)\]\.ratings\.(google|facebook|yelp|averageRating)$/
        );
        if (practiceRatingsMatches && editSellerProfile && formRef.current) {
            const [, indexStr, key] = practiceRatingsMatches;
            const practiceIndex = parseInt(indexStr, 10);
            const rounded = roundRating(value);
            const currentFormProfiles =
                formRef.current.values?.practiceProfiles ??
                editSellerProfile.practiceProfiles ??
                [];
            const updatedProfile = {
                ...editSellerProfile,
                practiceProfiles: currentFormProfiles.map(
                    (practice: PracticeProfileInfo, idx: number) => {
                        if (idx !== practiceIndex) {
                            return {
                                ...practice,
                                practiceLocations: practice.practiceLocations
                                    ? [...practice.practiceLocations]
                                    : [],
                                ratings: practice.ratings
                                    ? { ...practice.ratings }
                                    : {},
                            };
                        }
                        return {
                            ...practice,
                            practiceLocations: practice.practiceLocations
                                ? [...practice.practiceLocations]
                                : [],
                            ratings: {
                                ...(practice.ratings || {}),
                                [key]:
                                    rounded !== undefined
                                        ? parseFloat(rounded.toFixed(1))
                                        : undefined,
                            },
                        } as PracticeProfileInfo;
                    }
                ),
            };
            setEditSellerProfile(updatedProfile);
            formRef.current.setFieldValue(
                name,
                rounded !== undefined
                    ? rounded % 1 === 0
                        ? rounded.toString()
                        : rounded.toFixed(1)
                    : ''
            );
            return;
        }

        const practiceReviewCountMatches = name.match(
            /^practiceProfiles\[(\d+)\]\.ratings\.(googleReviewCount|facebookReviewCount|yelpReviewCount)$/
        );
        if (practiceReviewCountMatches && editSellerProfile && formRef.current) {
            const [, indexStr, key] = practiceReviewCountMatches;
            const practiceIndex = parseInt(indexStr, 10);
            const parsed = value === '' || value == null ? undefined : Math.max(0, Math.floor(Number(value)));
            const currentFormProfiles =
                formRef.current.values?.practiceProfiles ??
                editSellerProfile.practiceProfiles ??
                [];
            const updatedProfile = {
                ...editSellerProfile,
                practiceProfiles: currentFormProfiles.map(
                    (practice: PracticeProfileInfo, idx: number) => {
                        if (idx !== practiceIndex) {
                            return {
                                ...practice,
                                practiceLocations: practice.practiceLocations
                                    ? [...practice.practiceLocations]
                                    : [],
                                ratings: practice.ratings
                                    ? { ...practice.ratings }
                                    : {},
                            };
                        }
                        return {
                            ...practice,
                            practiceLocations: practice.practiceLocations
                                ? [...practice.practiceLocations]
                                : [],
                            ratings: {
                                ...(practice.ratings || {}),
                                [key]: parsed,
                            },
                        } as PracticeProfileInfo;
                    }
                ),
            };
            setEditSellerProfile(updatedProfile);
            formRef.current.setFieldValue(name, parsed !== undefined ? parsed : '');
            return;
        }

        const practiceMatches = name.match(/^practiceProfiles\[(\d+)\]\.(.+)$/);
        if (practiceMatches) {
            const [, index, field] = practiceMatches;
            const updatedProfile = updatePracticeProfile(
                parseInt(index),
                field,
                value
            );
            setEditSellerProfile(updatedProfile);
        }

        formRef.current.setFieldValue(name, value);
    };

    const getNextLocationNumber = (
        currentFormLocations: EntityLocationInfo[]
    ): number => {
        let nextLocationNumber = 1;
        if (currentFormLocations.length > 0) {
            const locationNumbers = currentFormLocations
                .map((location: EntityLocationInfo) => {
                    const match =
                        location.locationName?.match(/Location (\d+)/);
                    return match ? parseInt(match[1], 10) : 0;
                })
                .filter((num: number) => !isNaN(num));

            if (locationNumbers.length > 0) {
                const maxNumber = Math.max(...locationNumbers);
                nextLocationNumber = maxNumber + 1;
            }
        }

        return nextLocationNumber;
    };

    const onAddLocationClicked = (practiceIndex: number): void => {
        const currentFormValues = formRef.current.values;
        const currentPracticeFormData =
            currentFormValues.practiceProfiles?.[practiceIndex];
        const currentFormLocations =
            currentPracticeFormData?.practiceLocations || [];

        // Parse existing location names to find the highest number
        const nextLocationNumber = getNextLocationNumber(currentFormLocations);

        const newLocation = {
            id: getEmptyGuid(),
            locationName: `Location ${nextLocationNumber}`,
            addressInfo: {
                id: getEmptyGuid(),
                streetAddress: '',
                city: '',
                state: '',
                zipCode: '',
            },
            locationType: LocationType.Urban,
            dbaName: '',
            fteDvms: 0,
            practiceFocusType: 1,
            otherPracticeType: '',
        };

        const updatedLocations = [...currentFormLocations, newLocation];
        const fieldName = `practiceProfiles[${practiceIndex}].practiceLocations`;

        // Update form state first to preserve existing data
        formRef.current.setFieldValue(fieldName, updatedLocations);

        // Update component state to match form state
        const updatedProfile = updatePracticeProfile(
            practiceIndex,
            'practiceLocations',
            updatedLocations
        );
        setEditSellerProfile(updatedProfile);
    };

    const removeLocation = (
        practiceIndex: number,
        locationIndex: number
    ): void => {
        const currentFormValues = formRef.current.values;
        const currentPracticeFormData =
            currentFormValues.practiceProfiles?.[practiceIndex];
        const currentFormLocations =
            currentPracticeFormData?.practiceLocations || [];
        const updatedLocations = currentFormLocations.filter(
            (location: EntityLocationInfo, index: number) =>
                index !== locationIndex
        );
        const updatedProfile = updatePracticeProfile(
            practiceIndex,
            'practiceLocations',
            updatedLocations
        );
        setEditSellerProfile(updatedProfile);
        const fieldName = `practiceProfiles[${practiceIndex}].practiceLocations`;
        formRef.current.setFieldValue(fieldName, updatedLocations);
    };

    const handleSubmit = (values: FormikValues) => {
        try {
            setDataSyncTrigger((prev) => prev + 1);
            const updatedProfile: MarketplaceSellerProfile = {
                ...sellerProfile,
                practiceProfiles: (values.practiceProfiles || []).map((p: PracticeProfileInfo) => ({
                    ...p,
                    ratings: roundRatings(p.ratings),
                })),
                type: 'marketplaceSellerProfile',
            };
            props.setIsSaving(true);

            dispatch(UpdateProfile(updatedProfile))
                .then(() => {
                    props.setIsSaving(false);
                    props.onSaveSuccess?.();
                })
                .catch(() => {
                    props.setIsSaving(false);
                    props.onSaveError?.();
                });
        } catch (error) {
            console.error('Form submission error:', error);
        }
    };

    useImperativeHandle(ref, () => ({
        handleSubmit: () => {
            if (formRef.current) {
                formRef.current.handleSubmit();
            }
        },
        getSelectedPracticeId: () => selectedPracticeIdRef.current,
    }));

    return (
        <div className="seller-my-profile-form">
            <Form
                innerRef={formRef}
                onSubmit={handleSubmit}
                initialValues={{
                    practiceProfiles: (editSellerProfile?.practiceProfiles || []).map(
                        (p) => ({ ...p, ratings: buildRatingsInitialValues(p.ratings) })
                    ),
                }}
                validationSchema={getSellerProfileValidationSchema(
                    axios,
                    zipCodeCache,
                    serviceFocuses
                )}>
                <MarketplacePracticeProfilesContainer
                    profileId={editSellerProfile?.id || ''}
                    practices={editSellerProfile?.practiceProfiles || []}
                    setFormFieldValue={updateFormValue}
                    onAddLocationClicked={onAddLocationClicked}
                    removeLocation={removeLocation}
                    isEditable={isEditable}
                    isAdmin={isAdmin}
                    isBroker={isBroker}
                    dataSyncTrigger={dataSyncTrigger}
                    timelineId={timelineId}
                    onSelectedPracticeChange={(id) => { selectedPracticeIdRef.current = id; }}
                />
            </Form>
        </div>
    );
});

export default SellerMyProfileForm;
